<script lang="ts" setup>
const { t } = useI18n();
const { sourcesUrl } = useRuntimeConfig().public;
const { getCollectibles, getCollectiblesCount } = useCollectiblesController();
const { trackEvent } = useGAnalytics();
const appStateStore = useAppStateStore();
const merchant = appStateStore.currentMerchant;
const route = useRoute();
const router = useRouter();
const filterParams = computed(() => (route.query.filter as string) || "");
const GFALExStore = useGFALExStore();
const isLoadingMore = ref<boolean>(false);
const page = ref<number>(1);
const pageSize = ref<number>(12);
const showListed = ref<boolean>(route.query.listed === "false" ? false : true);

useSeoMeta({
  title: `${t(`main.appMenu.market`)} ${"| " + t(`main.merchant.${merchant}`)}`,
  description: `${t("main.marketplaceSEO.description.market")}`,
  ogTitle: `${t(`main.appMenu.market`)} ${
    "| " + t(`main.merchant.${merchant}`)
  }`,
  ogDescription: `${t("main.marketplaceSEO.description.market")}`,
  ogImage: sourcesUrl + "/images/marketplace.jpg",
  twitterTitle: `${t(`main.appMenu.market`)} ${
    "| " + t(`main.merchant.${merchant}`)
  }`,
  twitterDescription: `${t("main.marketplaceSEO.description.market")}`,
  twitterImage: sourcesUrl + "/images/marketplace.jpg",
});

const { data, status, refresh, clear } = await useAsyncData(
  `get${merchant}MarketCollectibles`,
  async () => {
    const [collectibles, altTotal] = await Promise.all([
      getCollectibles(
        merchant,
        page.value,
        pageSize.value,
        showListed.value,
        filterParams.value
      ),
      getCollectiblesCount(merchant, !showListed.value, filterParams.value),
    ]);

    if (GFALExStore.GFALExRate === 0) {
      throw new Error("Server down");
    }

    return { collectibles, altTotal };
  },
  {
    default: () => ({
      collectibles: {
        records: [],
        total: 0,
        page: 0,
        totalPages: 0,
        ungroupedTotal: 0,
      },
      altTotal: 0,
    }),
    server: false,
    lazy: true,
  }
);

const loadMore = async () => {
  isLoadingMore.value = true;
  page.value++;
  const response = await getCollectibles(
    merchant,
    page.value,
    pageSize.value,
    showListed.value,
    filterParams.value
  );
  if (!response) {
    return;
  }
  data.value.collectibles.records = [
    ...data.value.collectibles.records,
    ...response.records,
  ];
  data.value.collectibles.page = response.page;
  data.value.collectibles.totalPages = response.totalPages;
  isLoadingMore.value = false;
};

useInfiniteScroll(
  window,
  async () => {
    loadMore();
  },
  {
    distance: 100,
    canLoadMore: () =>
      !isLoadingMore.value &&
      data.value.collectibles.total > data.value.collectibles.records.length &&
      data.value.collectibles.page < data.value.collectibles.totalPages,
  }
);

watch(filterParams, async () => {
  page.value = 1;
  clear();
  await refresh();
});

watch(showListed, async () => {
  router.push({
    query: {
      ...route.query,
      listed: showListed.value ? "true" : "false",
    },
  });
  page.value = 1;
  clear();
  await refresh();
});
</script>

<template>
  <CommonHeroMain />
  <LoaderCardGridSkeleton
    v-if="
      status === 'pending' || status === 'idle' || !appStateStore.areTidsLoaded
    " />
  <BaseLayoutItemGrid v-else>
    <template #components>
      <Toolbar
        v-if="status !== 'error'"
        v-model:show-listed="showListed"
        :store-items="data.collectibles.records"
        :item-number="
          data.collectibles.ungroupedTotal || data.collectibles.total
        "
        :alt-total="data.altTotal"
        :show-filters="true" />
      <CommonNotFoundServer v-if="status === 'error'" />
      <CommonNotFoundItems
        v-else-if="data.collectibles.total < 1"
        :msg="$t('main.storeMain.noItemsFound')" />
    </template>
    <template
      v-if="status === 'success' && data.collectibles.total > 0"
      #list-items>
      <template
        v-for="collectible in data.collectibles.records"
        :key="collectible.id">
        <CardMarketCollectible :collectible="collectible" />
      </template>
    </template>
  </BaseLayoutItemGrid>
  <div v-if="isLoadingMore" class="py-10 flex flex-col items-center gap-4">
    <p class="text-neutral-300 text-center">
      {{ $t("main.storeMain.loadingMore") }}
    </p>
    <LoaderSpinner size="2xl" />
  </div>
</template>
